/**
 * Conversation + Query + MessageView — FE-state shapes for the chat
 * surface. Backed by Zustand slices in `apps/client/src/features/chat/state/`.
 *
 * v1 mapping:
 *   - `Conversation` ← `app/components/contexts/ConversationContext.tsx`
 *     conversation entry (id, title, queries[], per-conv config)
 *   - `Query` ← v1 `Query` (chat.ts:466-480) — one user turn + the agent
 *     answer/messages it triggered.
 *   - `MessageView` ← FE renderable derived from the wire `Message` —
 *     keeps only what bubbles need; the full payload stays in `wirePayload`.
 *
 * Action-proposal + display payload types are kept as `unknown` here.
 * Phase 7 (HITL) + Phase 8 (CAD/displays) will narrow them at the call-site.
 */

import { z } from "zod";
import {
  BusinessDefinitionNeededDisplaySchema,
  ClarificationNeededDisplaySchema,
  DisplaySchema,
  GeneratedImageDisplaySchema,
  GeneratedSvgDisplaySchema,
  InlineChartDisplaySchema,
} from "../displays.js";
import { AndoSourceSchema, AndoRawContextSchema } from "./source.js";
import {
  ByzantineConflictSchema,
  ProvenanceItemSchema,
  ReasoningHopSchema,
} from "./reasoning.js";
import {
  ChatFileAttachmentSchema,
  ChatImageAttachmentSchema,
} from "./attachment.js";

// ─── Focus selector (May 2026) ────────────────────────────────────────────

export const ChatFocusSchema = z.enum(["standard", "structured", "full"]);
export type ChatFocus = z.infer<typeof ChatFocusSchema>;

/**
 * Quality/latency selector — May 26, 2026 two-tier collapse.
 *
 * - ``fast`` — current ``quick`` behaviour, default.
 * - ``deep`` — merged ``standard + expert`` (Pro model, recursion,
 *   thinking HIGH, debate). Surfaces in the composer as the "Cerca a fondo"
 *   pill toggle.
 *
 * The legacy ``SearchPower`` enum is retained as ``@deprecated`` so
 * external callers don't break during the rollout window.
 */
export const ChatModeSchema = z.enum(["fast", "deep"]);
export type ChatMode = z.infer<typeof ChatModeSchema>;

/** @deprecated May 26, 2026 — collapsed into ``ChatMode``. */
export const SearchPowerSchema = z.enum(["quick", "standard", "expert"]);
/** @deprecated May 26, 2026 — collapsed into ``ChatMode``. */
export type SearchPower = z.infer<typeof SearchPowerSchema>;

// ─── Per-conversation persisted config ────────────────────────────────────

/**
 * Sticky per-conversation document scope — picked through the composer's
 * SourceScopePicker (June 2026). Each entry pairs the BE document id
 * (Weaviate ``doc_id``) with a display label so chips can render without
 * a round-trip to the mention-search endpoint. At send time
 * ``useSendQuery`` projects this array onto the WS envelope's
 * ``mentions[]`` field with ``kind: "document"`` — the BE retrieval
 * pipeline reuses ``build_mention_filters`` exactly the same way as
 * inline ``@``-mentions, so no new wire field is needed.
 */
export const ScopedDocumentSchema = z.object({
  /** BE document id (== Weaviate ``doc_id`` / content_items.id). */
  id: z.string().min(1),
  /** Label shown in the composer chip + picker row. */
  display: z.string(),
  /** Optional muted secondary line — preserved across popover open
   *  cycles so the picker row stays informative when the document is
   *  no longer in the live search results. */
  subtitle: z.string().optional(),
  /** White-label source slug (``google_drive``/``onedrive``/…). The
   *  picker uses it to render the corner badge so the user always sees
   *  where a scoped file actually lives. */
  provider: z.string().optional(),
  /** Full folder path inside the source drive (e.g.
   *  ``/Acme/Q3/Fatture``). Preferred over ``subtitle`` when present. */
  folderPath: z.string().optional(),
});
export type ScopedDocument = z.infer<typeof ScopedDocumentSchema>;

/**
 * Ando Connect "interroga un'area" query scope (June 14, 2026).
 *
 * A DELIBERATE, persistent query mode — the user explicitly chose to ask a
 * shared Connect Space in THIS conversation (distinct from the ephemeral
 * "active context" of what's currently open). Carries the Space ``id`` (sent to
 * the BE as ``connect_space_id``; the agent answers ONLY from that Space's
 * granted, read-only slice) plus the ``name`` / ``ownerOrgName`` so the
 * read-only composer chip renders without an extra fetch.
 */
export const ConnectScopeSchema = z.object({
  /** ``connect_spaces.id`` — the only field sent on the wire. */
  id: z.string().min(1),
  /** Space display name for the composer chip. */
  name: z.string(),
  /** Owning org name ("collaborando con …"), when known. */
  ownerOrgName: z.string().nullable().optional(),
});
export type ConnectScope = z.infer<typeof ConnectScopeSchema>;

export const ConversationConfigSchema = z.object({
  /** May 26, 2026 — two-tier mode selector. Replaces ``searchPower`` +
   * ``thinkingMode``. Default ``fast``. */
  mode: ChatModeSchema.default("fast"),
  webSearchMode: z.boolean().default(false),
  focus: ChatFocusSchema.default("standard"),
  /** Selected enterprise/data connection ids (filters BE search). */
  selectedConnectionIds: z.array(z.string()).default([]),
  /** Ando Mode snapshot — pinned at first send to avoid mid-thread drift. */
  agentModeVersionId: z.string().nullable().default(null),
  /** Folder filter for Drive/Doc surfaces. */
  folderFilter: z.array(z.string()).default([]),
  /**
   * Sticky per-conversation document scope (June 2026). Paired with
   * ``folderFilter`` they form the "Ambito" composer pill — narrowing
   * retrieval to a curated subset of folders + files without typing
   * an inline ``@``-mention every turn.
   */
  scopedDocuments: z.array(ScopedDocumentSchema).default([]),
  /**
   * Ando Connect "interroga un'area" scope (June 14, 2026). ``null`` = normal
   * chat (own knowledge). When set, the turn answers ONLY from this shared
   * Space's granted slice (read-only); the BE re-validates membership.
   */
  connectSpace: ConnectScopeSchema.nullable().default(null),
  /**
   * TERRA β — experimental relational-RAG retrieval engine (Jul 2026).
   * Admin-only composer toggle; when ON the send pipeline stamps
   * ``retrieval_engine: "terra"`` on the WS envelope. The BE re-verifies
   * the platform-admin claim server-side before honouring it, so this
   * flag is a routing REQUEST, not an entitlement. Default OFF keeps the
   * wire shape byte-identical for everyone else.
   */
  terraMode: z.boolean().default(false),
  /** Infrastructure request only; backend current authority is mandatory. */
  tramaInfrastructure: z.enum(["current", "lakehouse"]).optional(),
});

export type ConversationConfig = z.infer<typeof ConversationConfigSchema>;

// ─── User feedback ────────────────────────────────────────────────────────

/**
 * Assistant-turn rating — the BE `query_feedback.score` convention:
 * 0 = thumbs-down, 1 = thumbs-up, 2 = heart (excellent). Persisted via
 * `POST /feedback/add` and read back through `ConversationDetail.feedback_map`
 * (query_id → score). `CheckConstraint("score >= 0 AND score <= 2")` BE-side.
 */
export const FeedbackValueSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
]);
export type FeedbackValue = z.infer<typeof FeedbackValueSchema>;

// ─── MessageView — what the UI actually renders ───────────────────────────
//
// A render-friendly slice of a wire Message. Each bubble in <Messages> maps
// to one MessageView. The original wire payload is kept in `wirePayload`
// so per-feature renderers (CAD, banking, structured-data) can reach back
// for their specialised shape without polluting this barrel.

/**
 * MessageView role — chat-surface specific. Distinct from the global
 * `MessageRole` in `domain/message.ts` which has wire-only values
 * (`assistant` vs the FE-renderable `agent`).
 */
export const ChatMessageRoleSchema = z.enum(["user", "agent", "system"]);
export type ChatMessageRole = z.infer<typeof ChatMessageRoleSchema>;

export const ChatMessageStatusSchema = z.enum([
  "sending",
  "streaming",
  "sent",
  "error",
  "interrupted",
]);
export type ChatMessageStatus = z.infer<typeof ChatMessageStatusSchema>;

export const MessageViewSchema = z.object({
  /** Server-assigned id (or client-uuid before BE confirms). */
  id: z.string(),
  conversationId: z.string(),
  queryId: z.string().optional(),
  role: ChatMessageRoleSchema,
  /** Markdown body — empty during early streaming, fills incrementally. */
  content: z.string().default(""),
  /** ISO-8601 timestamp. */
  timestamp: z.string(),
  status: ChatMessageStatusSchema.default("sent"),
  /** Agent answer extras. */
  sources: z.array(AndoSourceSchema).optional(),
  raw_context: z.array(AndoRawContextSchema).optional(),
  confidence_score: z.number().optional(),
  provenance: z.array(ProvenanceItemSchema).optional(),
  reasoning_chain: z.array(ReasoningHopSchema).optional(),
  /**
   * Dirty-data version conflicts the TRAMA consensus pass reconciled
   * (Jul 2026). Populated from ``provenance_map.byzantine_conflicts`` on the
   * live answer frame and the history rebuild. Rendered inline by
   * ``<ConflictNote>`` under the agent turn.
   */
  byzantine_conflicts: z.array(ByzantineConflictSchema).readonly().optional(),
  /** User-turn attachments (April + May 2026). */
  image_attachments: z.array(ChatImageAttachmentSchema).optional(),
  file_attachments: z.array(ChatFileAttachmentSchema).optional(),
  /**
   * Sticky scope mentions persisted on the USER turn so the chat
   * history shows exactly WHICH folders/files were included in the
   * retrieval at send time (June 2026). Mirrors the wire ``mentions[]``
   * field merged at send by ``useSendQuery``. The renderer surfaces
   * them as muted chips below the bubble — same visual family as
   * ``file_attachments`` so the user immediately recognises "this
   * message was scoped to X".
   */
  mentions: z
    .array(
      z.object({
        kind: z.enum(["document", "folder", "connector", "person", "thread"]),
        id: z.string(),
        display: z.string(),
        /** White-label provider slug (drive vendor / source family). */
        provider: z.string().optional(),
        /** Folder path inside the source — relevant for document chips. */
        folderPath: z.string().optional(),
      }),
    )
    .optional(),
  /**
   * Structured-data results carried on agent ``answer`` turns. Populated
   * from ``WSChatEvent.displays`` (live stream) or the conversation
   * rebuild frame (history). The renderer dispatches on
   * ``display.source_type`` — see PR-A2 for the inline ``<StructuredDataCard>``
   * and PR-A3 for the side ``<EnterpriseDataPanel>``.
   *
   * Marked ``readonly`` so feature slices can't mutate a Display in-place
   * (the chat store hands the array straight to TanStack Query consumers).
   */
  displays: z.array(DisplaySchema).readonly().optional(),
  /**
   * Agent-generated IMAGE (``generate_image`` tool). A separate media family
   * from the structured ``displays`` union — persisted here so the chat shows
   * a clickable thumbnail and the working area auto-opens it (June 2026).
   */
  generated_image: GeneratedImageDisplaySchema.optional(),
  /**
   * Agent-generated SVG (``generate_svg`` tool). Mirror of ``generated_image``
   * — same chat-chip + working-area auto-open contract. Kept as its own
   * field so the FE doesn't have to discriminate by MIME on a single media
   * slot. June 14, 2026.
   */
  generated_svg: GeneratedSvgDisplaySchema.optional(),
  /**
   * Agent-emitted inline chart (RAG-classic mode). The structured-data
   * ``displays`` union doesn't carry this because it isn't a connector
   * result — it's an answer-surface visualisation synthesised by the LLM.
   * Surfaced as its own field so the FE chip + working-area panel can
   * mount a dedicated viewer that converts the BE payload to ECharts
   * without re-discriminating the structured-data routes. June 14, 2026.
   */
  inline_chart: InlineChartDisplaySchema.optional(),
  /**
   * Clarification request (Aug 2026). The connector REFUSED to guess between
   * two equally plausible readings of the question ("importo" → "Importo
   * Lordo" vs "Importo Netto") and handed back the choice. Its own field for
   * the same reason as ``inline_chart``: no ``source_type``, no rows — it
   * isn't a result, so it can't live in the ``displays`` union.
   *
   * INLINE lifecycle: rendered under the agent turn, and clicking an option
   * just sends ``suggested_query`` as a fresh user message. Deliberately NOT
   * modelled as a pending action (cf. ``features/source-confirmation``, which
   * POSTs a confirmation to unblock a Temporal fork) — nothing is blocked
   * server-side here, the turn already completed.
   */
  clarification: ClarificationNeededDisplaySchema.optional(),
  business_definition_needed: BusinessDefinitionNeededDisplaySchema.optional(),
  /** Feedback the user gave on this assistant turn. */
  feedback: FeedbackValueSchema.optional(),
  /** System-message label (e.g. "Errore", "Connessione persa"). */
  systemLabel: z.string().optional(),
  /** Original wire payload, opaque — feature-specific renderers reach back here. */
  wirePayload: z.unknown().optional(),
});

export type MessageView = z.infer<typeof MessageViewSchema>;

// ─── Query — one user-turn record ─────────────────────────────────────────
//
// A Query groups the user's input message + the messages the agent emitted
// in response (text chunks, results, action_proposal, reasoning_hop, etc).
// Query lifecycle: created on send → finished when `complete`/`stopped`/error.

export const QuerySchema = z.object({
  id: z.string(),
  conversationId: z.string(),
  /** The user's input text. */
  query: z.string(),
  finished: z.boolean().default(false),
  query_start: z.string(),
  query_end: z.string().nullable().default(null),
  /** 0 = down / 1 = up / 2 = heart user feedback (BE `query_feedback.score`). */
  feedback: FeedbackValueSchema.nullable().default(null),
  /** Ordered messages this query produced (user msg + agent replies). */
  messages: z.array(MessageViewSchema).default([]),
  image_attachments: z.array(ChatImageAttachmentSchema).optional(),
  file_attachments: z.array(ChatFileAttachmentSchema).optional(),
});

export type Query = z.infer<typeof QuerySchema>;

// ─── Conversation — sidebar entry + history ───────────────────────────────

/**
 * Full chat-state conversation — the FE-state shape with all queries
 * loaded. Distinct from `Conversation` (sidebar entry) in
 * `domain/project.ts`, which is the lightweight summary the list view
 * uses.
 */
export const ChatConversationSchema = z.object({
  id: z.string(),
  title: z.string().default(""),
  user_id: z.string(),
  /** Ordered queries — chronological. */
  queries: z.array(QuerySchema).default([]),
  /** Updated when any query mutates. */
  updated_at: z.string(),
  /** Per-conversation persisted UX config. */
  config: ConversationConfigSchema.default({} as ConversationConfig),
  /** True while history replay is in progress (suspends new sends). */
  replayInProgress: z.boolean().default(false),
  /** Soft-error flag if the last query interrupted. */
  error: z.string().optional(),
  /**
   * Owning project (May 22, 2026). ``null`` for legacy / unassigned chats
   * — the sidebar groups those under "Senza progetto". Optional in the
   * wire shape because the BE migration adding the column is rolling out;
   * once every row carries it we can tighten to non-nullable.
   */
  project_id: z.string().nullable().optional(),
});

export type ChatConversation = z.infer<typeof ChatConversationSchema>;

// ─── Interrupted query — Jan 2026 retry UI ────────────────────────────────

export const InterruptedQuerySchema = z.object({
  user_id: z.string(),
  query: z.string(),
  conversation_id: z.string(),
  query_id: z.string(),
  /** Epoch ms when the disconnect happened. */
  timestamp: z.number(),
  /** Snapshot of the config used so retry resumes the exact same mode. */
  config: ConversationConfigSchema.optional(),
});

export type InterruptedQuery = z.infer<typeof InterruptedQuerySchema>;
